import React, { useLayoutEffect, useState, useRef } from 'react';
import { HOT_KEY } from '@web-chat-core/ui-kit';
import { useHorizontalScroll } from '../../hooks/useHorizontalScroll';
import Typography from '../Typography';
import { TabsWrapper, Tab, TabsScroll, BorderBottomHelper } from './view';

export interface TabProps {
    tabName: string;
    tabId: string;
    content: React.ReactNode;
    disabled?: boolean;
    cy?: string;
}

export enum TabsMod {
    Default = 'default',
    Search = 'search',
}

interface TabsProps {
    tabs: TabProps[];
    defaultActiveTab?: string;
    activeTab?: string;
    mod?: TabsMod;
    withEnterHotKey?: boolean;
    onSetActiveTab?: (activeTab: string) => void;
}

export const Tabs = ({
    tabs,
    defaultActiveTab = '',
    activeTab,
    withEnterHotKey = false,
    mod = TabsMod.Default,
    onSetActiveTab,
}: TabsProps) => {
    const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab);
    const tabScrollRef = useHorizontalScroll<HTMLDivElement>();
    const activeTabRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (tabScrollRef.current) {
            activeTabRef.current?.scrollIntoView();
        }
    }, [activeTab]);

    const handleTabClick = (tabId: string) => () => {
        if (activeTab === undefined) {
            setInternalActiveTab(tabId);
        }

        onSetActiveTab && onSetActiveTab(tabId);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key.toLocaleLowerCase() === HOT_KEY.ENTER) {
            const target = event.target as HTMLElement;
            const tabId = target.dataset?.tabId;
            if (tabId) {
                handleTabClick(tabId)();
            }
        }
    };

    const realTabId = activeTab !== undefined ? activeTab : internalActiveTab;
    const withEnterHotKeyAttr = withEnterHotKey
        ? {
              tabIndex: 0,
              role: 'button',
              withEnterHotKey,
              onKeyDown: handleKeyDown,
          }
        : {};

    return (
        <>
            <TabsWrapper>
                <TabsScroll
                    mod={mod as TabsMod}
                    ref={tabScrollRef}
                >
                    {tabs.map(tab =>
                        tab.disabled ? null : (
                            <Tab
                                isActive={tab.tabId === realTabId}
                                key={tab.tabId}
                                onClick={handleTabClick(tab.tabId)}
                                disabled={tab.disabled}
                                ref={tab.tabId === realTabId ? activeTabRef : null}
                                data-cy={tab.cy}
                                data-tab-id={tab.tabId}
                                {...withEnterHotKeyAttr}
                            >
                                <Typography
                                    variant={mod === TabsMod.Search ? 'body2' : 'text2'}
                                    colorVariant={tab.tabId === realTabId ? 'base' : 'second'}
                                >
                                    {tab.tabName}
                                </Typography>
                                <BorderBottomHelper isActive={tab.tabId === realTabId} />
                            </Tab>
                        ),
                    )}
                </TabsScroll>
            </TabsWrapper>
            {tabs.find(tab => tab.tabId === realTabId)?.content || null}
        </>
    );
};

export default Tabs;
